import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { FaArrowRight, FaMapMarkerAlt, FaEnvelope } from 'react-icons/fa';
import useScrollTo from '../hooks/useScrollTo';
import SEOHelmet from '../components/SEO/SEOHelmet';
import { LocalBusinessSchema, BreadcrumbSchema } from '../components/SEO/schemas';

const locationsData = {
  'minnesota': {
    title: 'Minnesota Software Development Company',
    metaTitle: 'Software Development Company Minnesota | Custom Software & Web Development MN',
    metaDescription: 'Leading software development company serving Minnesota. CitiTekPro provides custom software development, web development, mobile apps, SEO, and IT consulting for businesses across the Twin Cities and greater Minnesota.',
    keywords: 'software development company Minnesota, custom software Minnesota, web development MN, IT consulting Minnesota, Minnesota software developers, digital agency Minnesota, Twin Cities software development',
    headline: 'Minnesota Software Development',
    subheadline: 'Your Trusted Technology Partner in the North Star State',
    description: 'CitiTekPro is a full-service digital agency serving businesses throughout Minnesota. From the Twin Cities to Greater Minnesota, we deliver custom software development, web applications, mobile apps, and IT consulting that help Minnesota businesses thrive in the digital age.',
    locationName: 'Minnesota',
    region: 'Minnesota',
    cities: ['Minneapolis', 'Saint Paul', 'Rochester', 'Duluth', 'Bloomington', 'Brooklyn Park', 'Plymouth', 'Maple Grove'],
    whyChoose: [
      'Local Minnesota team that understands your market and community',
      'Proven track record with MN businesses across industries',
      'Comprehensive digital solutions from strategy to implementation',
      'Ongoing support and partnership for long-term success',
      'Expertise in the latest technologies and best practices',
      'Commitment to the Minnesota business community'
    ]
  },
  'minneapolis': {
    title: 'Minneapolis Software Development Company',
    metaTitle: 'Software Development Company Minneapolis MN | Web Development & IT Services',
    metaDescription: 'Top software development company in Minneapolis, MN. CitiTekPro delivers custom software, web development, mobile apps, and IT consulting for Minneapolis businesses. Located at 2910 Pillsbury Ave, Suite 31.',
    keywords: 'software development Minneapolis, web development Minneapolis, Minneapolis software company, custom software Minneapolis MN, IT consulting Minneapolis, digital agency Minneapolis, web design Minneapolis',
    headline: 'Minneapolis Software Development',
    subheadline: 'Empowering Minneapolis Businesses with Technology',
    description: 'Based in Minneapolis at 2910 Pillsbury Ave Suite 31, CitiTekPro serves businesses throughout the city with cutting-edge software development and IT solutions. Whether you\'re in downtown Minneapolis, the Karmel Mall area, Lake Street, Cedar-Riverside, or surrounding neighborhoods, we\'re your local partner for digital transformation.',
    locationName: 'Minneapolis',
    region: 'Minnesota',
    cities: ['Minneapolis'],
    whyChoose: [
      'Based in Minneapolis — we understand the local business landscape',
      'Serving Karmel Mall, Lake Street, and Cedar-Riverside communities',
      'Fast response times and local in-person support available',
      'Experience with Minneapolis industries from healthcare to retail',
      'Modern development practices and emerging technologies',
      'Long-term partnerships with Minneapolis businesses and organizations'
    ]
  },
  'st-paul': {
    title: 'St. Paul Software Development Company',
    metaTitle: 'Software Development Company St. Paul MN | Custom Software & Web Development',
    metaDescription: 'Professional software development company serving St. Paul, MN. CitiTekPro provides custom software, web development, mobile apps, SEO, and IT consulting for St. Paul businesses and organizations.',
    keywords: 'software development St. Paul, web development St. Paul MN, St. Paul software company, custom software St Paul, IT consulting St. Paul, digital agency St. Paul Minnesota',
    headline: 'St. Paul Software Development',
    subheadline: 'Digital Solutions for Minnesota\'s Capital City',
    description: 'CitiTekPro proudly serves businesses in St. Paul with comprehensive software development and technology services. From downtown St. Paul to the surrounding communities, we help organizations leverage technology to achieve their business goals.',
    locationName: 'St. Paul',
    region: 'Minnesota',
    cities: ['St. Paul'],
    whyChoose: [
      'Serving the St. Paul business community with tailored solutions',
      'Deep understanding of St. Paul\'s unique business environment',
      'Custom solutions for government, healthcare, and commercial sectors',
      'Local support and dedicated account management',
      'Proven success with St. Paul organizations across industries',
      'Commitment to the Twin Cities region and its growth'
    ]
  },
  'bloomington': {
    title: 'Bloomington MN Software Development Company',
    metaTitle: 'Software Development Bloomington MN | Web Development & IT Services',
    metaDescription: 'Professional software development and web design services in Bloomington, MN. CitiTekPro builds custom software, websites, and mobile apps for Bloomington businesses near Mall of America and the Twin Cities.',
    keywords: 'software development Bloomington MN, web development Bloomington Minnesota, IT consulting Bloomington, digital agency Bloomington MN, custom software Bloomington',
    headline: 'Bloomington Software Development',
    subheadline: 'Digital Solutions for Bloomington Businesses',
    description: 'CitiTekPro serves businesses throughout Bloomington, Minnesota with custom software development, web design, and IT consulting. Conveniently serving the greater Twin Cities south metro including Mall of America area businesses and Bloomington\'s thriving commercial corridor.',
    locationName: 'Bloomington',
    region: 'Minnesota',
    cities: ['Bloomington'],
    whyChoose: [
      'Serving Bloomington and the Twin Cities south metro',
      'Experience with retail, hospitality, and commercial businesses',
      'Custom software tailored to Bloomington\'s business environment',
      'Fast, responsive local support and project management',
      'Full-service from design to deployment and ongoing maintenance',
      'Competitive pricing for small and mid-sized Bloomington businesses'
    ]
  },
  'brooklyn-park': {
    title: 'Brooklyn Park MN Software Development Company',
    metaTitle: 'Software Development Brooklyn Park MN | Web Development & IT Services',
    metaDescription: 'Custom software development and web design in Brooklyn Park, MN. CitiTekPro provides IT consulting, web development, mobile apps, and business automation for Brooklyn Park businesses.',
    keywords: 'software development Brooklyn Park MN, web development Brooklyn Park Minnesota, IT consulting Brooklyn Park, digital agency Brooklyn Park, custom software Brooklyn Park MN',
    headline: 'Brooklyn Park Software Development',
    subheadline: 'Technology Solutions for Brooklyn Park Businesses',
    description: 'CitiTekPro delivers professional software development and IT services to businesses in Brooklyn Park, Minnesota. From custom web applications to business automation, we help Brooklyn Park companies compete and grow in today\'s digital landscape.',
    locationName: 'Brooklyn Park',
    region: 'Minnesota',
    cities: ['Brooklyn Park'],
    whyChoose: [
      'Serving Brooklyn Park and northwest Twin Cities businesses',
      'Tailored digital solutions for diverse local industries',
      'Experienced team with a proven track record in web development',
      'Business automation reducing overhead and increasing efficiency',
      'SEO services to help Brooklyn Park businesses get found online',
      'Dedicated support and long-term partnership approach'
    ]
  },
  'brooklyn-center': {
    title: 'Brooklyn Center MN Software Development Company',
    metaTitle: 'Software Development Brooklyn Center MN | Web Design & IT Consulting',
    metaDescription: 'Professional software development and web design for Brooklyn Center, MN businesses. CitiTekPro provides custom software, IT consulting, SEO, and business automation services.',
    keywords: 'software development Brooklyn Center MN, web development Brooklyn Center Minnesota, IT consulting Brooklyn Center, digital services Brooklyn Center, custom software Brooklyn Center MN',
    headline: 'Brooklyn Center Software Development',
    subheadline: 'Digital Solutions for Brooklyn Center Businesses',
    description: 'CitiTekPro is proud to serve businesses in Brooklyn Center, Minnesota with professional software development, web design, and technology consulting. We help local businesses build a stronger digital presence and streamline their operations.',
    locationName: 'Brooklyn Center',
    region: 'Minnesota',
    cities: ['Brooklyn Center'],
    whyChoose: [
      'Serving Brooklyn Center and the north metro Twin Cities area',
      'Affordable custom software and web development for local businesses',
      'SEO optimization to help you rank for Brooklyn Center searches',
      'Business automation to improve your workflow and reduce costs',
      'Responsive design across all devices and screen sizes',
      'Ongoing support and maintenance programs available'
    ]
  },
  'plymouth': {
    title: 'Plymouth MN Software Development Company',
    metaTitle: 'Software Development Plymouth MN | Custom Software & Web Design Minnesota',
    metaDescription: 'Expert software development and web design in Plymouth, MN. CitiTekPro builds custom software, mobile apps, and delivers IT consulting for Plymouth businesses in the Twin Cities west metro.',
    keywords: 'software development Plymouth MN, web development Plymouth Minnesota, IT consulting Plymouth, custom software Plymouth MN, digital agency Plymouth, web design Plymouth',
    headline: 'Plymouth Software Development',
    subheadline: 'Innovative Technology for Plymouth Businesses',
    description: 'CitiTekPro serves Plymouth businesses with enterprise-grade software development, responsive web design, and strategic IT consulting. Located in the prosperous Twin Cities west metro, Plymouth businesses benefit from our full range of digital services and local expertise.',
    locationName: 'Plymouth',
    region: 'Minnesota',
    cities: ['Plymouth'],
    whyChoose: [
      'Serving Plymouth and the Twin Cities west metro corridor',
      'Custom software solutions for professional services and enterprise',
      'Mobile-first web development built for modern business needs',
      'Data analytics and business intelligence dashboards',
      'IT strategy consulting aligned with your business objectives',
      'Transparent project management and dedicated communication'
    ]
  },
  'maple-grove': {
    title: 'Maple Grove MN Software Development Company',
    metaTitle: 'Software Development Maple Grove MN | Web Design & IT Services Minnesota',
    metaDescription: 'Professional software development and web design in Maple Grove, MN. CitiTekPro delivers custom software, SEO, mobile apps, and IT consulting for Maple Grove businesses.',
    keywords: 'software development Maple Grove MN, web development Maple Grove Minnesota, IT consulting Maple Grove, custom software Maple Grove, digital agency Maple Grove MN',
    headline: 'Maple Grove Software Development',
    subheadline: 'Digital Excellence for Maple Grove Businesses',
    description: 'CitiTekPro delivers top-tier software development and digital solutions for businesses in Maple Grove, Minnesota. As one of the fastest-growing communities in the Twin Cities metro, Maple Grove businesses deserve technology partners who can match their ambitions.',
    locationName: 'Maple Grove',
    region: 'Minnesota',
    cities: ['Maple Grove'],
    whyChoose: [
      'Serving Maple Grove and the Twin Cities northwest suburbs',
      'Custom web applications tailored to your industry and processes',
      'Local SEO to help Maple Grove customers find your business',
      'Business automation for growing Maple Grove companies',
      'Modern, mobile-first design for an exceptional user experience',
      'Scalable solutions that grow alongside your business'
    ]
  },
  'richfield': {
    title: 'Richfield MN Software Development Company',
    metaTitle: 'Software Development Richfield MN | Web Design & IT Consulting Minnesota',
    metaDescription: 'Custom software development and web design in Richfield, MN. CitiTekPro provides professional IT consulting, mobile apps, SEO, and business automation for Richfield businesses.',
    keywords: 'software development Richfield MN, web development Richfield Minnesota, IT consulting Richfield, custom software Richfield MN, digital services Richfield',
    headline: 'Richfield Software Development',
    subheadline: 'Smart Digital Solutions for Richfield Businesses',
    description: 'CitiTekPro serves the Richfield business community with professional software development, web design, and IT services. Conveniently located just minutes from Richfield, we provide the digital tools and strategies that help local businesses succeed online and offline.',
    locationName: 'Richfield',
    region: 'Minnesota',
    cities: ['Richfield'],
    whyChoose: [
      'Serving Richfield and the Minneapolis south metro',
      'Affordable web development and custom software for local businesses',
      'SEO services to help your Richfield business rank on Google',
      'Mobile app development for modern business needs',
      'Business automation reducing manual work and operational costs',
      'Friendly, responsive team with consistent communication'
    ]
  },
  'burnsville': {
    title: 'Burnsville MN Software Development Company',
    metaTitle: 'Software Development Burnsville MN | Web Design & IT Services Minnesota',
    metaDescription: 'Professional software development and web design for Burnsville, MN businesses. CitiTekPro delivers custom software, IT consulting, mobile apps, SEO, and business automation.',
    keywords: 'software development Burnsville MN, web development Burnsville Minnesota, IT consulting Burnsville, custom software Burnsville MN, digital agency Burnsville',
    headline: 'Burnsville Software Development',
    subheadline: 'Technology-Driven Growth for Burnsville Businesses',
    description: 'CitiTekPro provides Burnsville businesses with reliable, high-quality software development and IT solutions. Serving the Twin Cities south metro, we help Burnsville companies build powerful digital platforms, improve operational efficiency, and grow their online presence.',
    locationName: 'Burnsville',
    region: 'Minnesota',
    cities: ['Burnsville'],
    whyChoose: [
      'Serving Burnsville and the Twin Cities south metro',
      'Custom software and web applications for every business size',
      'Data-driven SEO strategies for better local search visibility',
      'Business process automation to increase efficiency',
      'Cybersecurity and infrastructure management services',
      'Long-term technology partnerships and ongoing support'
    ]
  },
  'eagan': {
    title: 'Eagan MN Software Development Company',
    metaTitle: 'Software Development Eagan MN | Custom Software & Web Development Minnesota',
    metaDescription: 'Expert software development and IT consulting in Eagan, MN. CitiTekPro builds custom software, responsive websites, mobile apps, and delivers SEO services for Eagan businesses.',
    keywords: 'software development Eagan MN, web development Eagan Minnesota, IT consulting Eagan, custom software Eagan MN, digital agency Eagan, web design Eagan',
    headline: 'Eagan Software Development',
    subheadline: 'Cutting-Edge Digital Solutions for Eagan Businesses',
    description: 'CitiTekPro is the technology partner Eagan businesses trust for custom software development, responsive web design, and strategic IT consulting. With a proven record across the Twin Cities metro, we bring enterprise-grade solutions to businesses of all sizes in Eagan.',
    locationName: 'Eagan',
    region: 'Minnesota',
    cities: ['Eagan'],
    whyChoose: [
      'Serving Eagan and the Twin Cities south metro area',
      'Enterprise-grade software at accessible prices for local businesses',
      'Custom CRM and SaaS development for Eagan organizations',
      'Local SEO and digital marketing to attract more customers',
      'Reliable managed IT services and cybersecurity solutions',
      'Dedicated project management with consistent delivery'
    ]
  },
  'edina': {
    title: 'Edina MN Software Development Company',
    metaTitle: 'Software Development Edina MN | Premium Web Design & IT Consulting Minnesota',
    metaDescription: 'Premium software development and web design in Edina, MN. CitiTekPro delivers custom software, mobile apps, IT consulting, and SEO services for Edina businesses and professional services firms.',
    keywords: 'software development Edina MN, web development Edina Minnesota, IT consulting Edina, custom software Edina MN, web design Edina, digital agency Edina',
    headline: 'Edina Software Development',
    subheadline: 'Premium Technology Solutions for Edina Businesses',
    description: 'CitiTekPro delivers premium software development, elegant web design, and strategic IT consulting for businesses in Edina, Minnesota. From 50th & France to the broader Edina business community, we craft digital solutions that reflect the quality and professionalism your clients expect.',
    locationName: 'Edina',
    region: 'Minnesota',
    cities: ['Edina'],
    whyChoose: [
      'Serving Edina and the Minneapolis southwest suburbs',
      'High-quality custom software and web development for professional services',
      'Polished, conversion-optimized web design for discerning brands',
      'Comprehensive SEO to help Edina businesses dominate local search',
      'Analytics dashboards providing real business intelligence',
      'White-glove account management and concierge-level support'
    ]
  },
  'roseville': {
    title: 'Roseville MN Software Development Company',
    metaTitle: 'Software Development Roseville MN | Web Design & IT Services Minnesota',
    metaDescription: 'Professional software development and web design for Roseville, MN businesses. CitiTekPro provides custom software, IT consulting, mobile apps, and SEO for Roseville and surrounding Minnesota communities.',
    keywords: 'software development Roseville MN, web development Roseville Minnesota, IT consulting Roseville, custom software Roseville MN, digital agency Roseville',
    headline: 'Roseville Software Development',
    subheadline: 'Digital Solutions for Roseville Businesses',
    description: 'CitiTekPro serves businesses in Roseville, Minnesota with professional software development, web design, and IT consulting services. Strategically located between Minneapolis and Saint Paul, Roseville businesses benefit from our comprehensive digital services and local Twin Cities expertise.',
    locationName: 'Roseville',
    region: 'Minnesota',
    cities: ['Roseville'],
    whyChoose: [
      'Serving Roseville and the Minneapolis-Saint Paul inner ring suburbs',
      'Custom software and web development for diverse local industries',
      'SEO optimization targeted to Roseville and nearby communities',
      'Business automation reducing overhead and improving workflows',
      'Experienced team with a full-service approach to digital growth',
      'Reliable ongoing support and maintenance programs'
    ]
  }
};

const LocationLandingPage = () => {
  const { slug } = useParams();
  const { scrollToSection } = useScrollTo();
  const location = locationsData[slug];

  if (!location) {
    return <Navigate to="/" replace />;
  }

  const services = [
    { label: 'Custom Software Development', slug: 'custom-software-development' },
    { label: 'Web Development & Design', slug: 'web-development' },
    { label: 'Mobile App Development', slug: 'mobile-app-development' },
    { label: 'SEO Services', slug: 'seo-services' },
    { label: 'IT Consulting', slug: 'it-consulting' },
    { label: 'Business Automation', slug: 'business-automation' },
    { label: 'AI Software Development', slug: 'ai-software-development' },
    { label: 'MERN Stack Development', slug: 'mern-stack-development' },
    { label: 'SaaS Application Development', slug: 'saas-development' },
    { label: 'Custom CRM Development', slug: 'custom-crm-development' }
  ];

  return (
    <>
      <SEOHelmet
        title={location.metaTitle}
        description={location.metaDescription}
        keywords={location.keywords}
        canonicalUrl={`https://cititekpro.com/locations/${slug}`}
        ogType="place"
      />
      <LocalBusinessSchema />
      <BreadcrumbSchema
        breadcrumbs={[
          { name: 'Home', url: 'https://cititekpro.com/' },
          { name: 'Locations', url: 'https://cititekpro.com/#about' },
          { name: location.locationName, url: `https://cititekpro.com/locations/${slug}` }
        ]}
      />

      <div className="relative pt-16 min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -left-40 w-96 h-96 bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-40 -right-40 w-96 h-96 bg-teal/5 dark:bg-primary/5 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          {/* Page Header */}
          <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 dark:bg-teal/10 rounded-full mb-6">
              <FaMapMarkerAlt className="text-primary dark:text-teal" />
              <span className="text-sm font-semibold text-primary dark:text-teal">
                Serving {location.locationName}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-gray-900 dark:text-white">{location.headline}</span>
            </h1>
            <p className="text-xl md:text-2xl text-primary dark:text-teal font-semibold mb-6">
              {location.subheadline}
            </p>
            <div className="h-1.5 w-24 mx-auto bg-gradient-to-r from-primary via-teal to-accent rounded-full mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {location.description}
            </p>
          </div>

          {/* Services Grid */}
          <div className="mb-12 md:mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
              Our Services in {location.locationName}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-2 h-2 bg-primary dark:bg-teal rounded-full group-hover:scale-150 transition-transform duration-300 flex-shrink-0"></div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">{service.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-12 md:mb-16 bg-white dark:bg-gray-800 p-8 md:p-12 rounded-2xl shadow-xl">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
              Why Choose CitiTekPro in {location.locationName}?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {location.whyChoose.map((reason, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 dark:bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-primary dark:bg-teal"></div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">{reason}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-primary to-teal dark:from-teal dark:to-primary p-12 rounded-2xl shadow-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your {location.locationName} Business?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how CitiTekPro can help your {location.locationName} business succeed with custom software and digital solutions.
            </p>
            <button
              onClick={() => scrollToSection('consultation')}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary dark:text-teal font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Get Your Free Consultation
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>

          {/* Contact & NAP */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-200 dark:border-gray-700">
              <FaMapMarkerAlt className="text-3xl text-primary dark:text-teal mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Our Office</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                2910 Pillsbury Ave, Suite 31<br />
                Minneapolis, MN 55408
              </p>
            </div>
            <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-200 dark:border-gray-700">
              <FaEnvelope className="text-3xl text-primary dark:text-teal mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Email Us</h3>
              <a
                href="mailto:contact@cititekpro.com"
                className="text-primary dark:text-teal hover:underline text-sm"
              >
                contact@cititekpro.com
              </a>
            </div>
            <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-200 dark:border-gray-700">
              <FaMapMarkerAlt className="text-3xl text-primary dark:text-teal mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Service Area</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {location.locationName}, {location.region}<br />
                & Twin Cities Metro
              </p>
            </div>
          </div>

          {/* Areas Served */}
          {location.cities.length > 1 && (
            <div className="mt-12 text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Cities We Serve in {location.locationName}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {location.cities.join(' • ')}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationLandingPage;
